import React, { useEffect } from 'react';
import { EInHomeProductCategory, ProductCategory } from 'api/types/product';
import { Controller, useFormContext } from 'react-hook-form';
import { Radio, RadioGroup, Spinner, TextField } from 'ui-kit';
import FormControl from 'ui-kit/FormControl';
import QuantityField from './QuantityField';
import AlertTresholdField from './AlertThresholdField';
import DelegatesField from './DelegatesField';
import './ProductSettingsBaseForm.scss';
import ExtraFields from './ExtraFields';
import { useOwnerDetails } from 'api/owners';
import { PaymentPlan, paymentPlanOrder } from 'api/types/owner';
import _ from 'lodash';
import { useSignedUser } from 'api/auth';

const BASE_CLASS = 'ProductSettingsBaseForm';

export default function ProductSettingsBaseForm() {
  const { control, watch, setError, setValue } = useFormContext();
  const { data: user } = useSignedUser();
  const { data, isLoading } = useOwnerDetails(user?.owner_id);
  const { plan } = data || {};

  const productCategory = watch('product.category');
  const showDeliveryDate = watch('product.showDeliveryDate');

  useEffect(() => {
    if (productCategory === ProductCategory.IH) {
      setValue(
        'product.category',
        showDeliveryDate
          ? EInHomeProductCategory.IH_WITH_DATE
          : EInHomeProductCategory.IH_WITHOUT_DATE,
        { shouldValidate: true, shouldDirty: true },
      );
    }
  }, [productCategory, showDeliveryDate]);

  const handleCategoryChange = (e) => {
    const selectedCategory = e.target.value;
    // When changing to immediate availability, we should reset preorderBy to 0
    if (
      selectedCategory === EInHomeProductCategory.IH_WITHOUT_DATE ||
      selectedCategory === EInHomeProductCategory.IH_WITH_DATE
    ) {
      setValue('product.preorderBy', 0, { shouldDirty: true });
      // If the selected category is one of IH_WITH_DATE or IH_WITHOUT_DATE, we need to set the cateogry as IH
      // setValue('product.category', ProductCategory.IH, { shouldValidate: true, shouldDirty: true });
      // and set the showDeliveryDate to true or false based on the selected category
      setValue(
        'product.showDeliveryDate',
        selectedCategory === EInHomeProductCategory.IH_WITH_DATE,
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    } else {
      // If the selected category is not IH, we need to set the showDeliveryDate to true
      setValue('product.showDeliveryDate', true, { shouldDirty: true });
    }
    setValue('product.category', selectedCategory, { shouldValidate: true, shouldDirty: true });
    setValue('product.requiresApproval', false, { shouldDirty: true });
  };

  if (isLoading) {
    return <Spinner />;
  }
  return (
    <div className={BASE_CLASS}>
      <Controller
        control={control}
        name="product.category"
        render={({ field }) => {
          return (
            <FormControl label="Is this product available immediately after purchase?" required>
              <RadioGroup {...field} onChange={handleCategoryChange}>
                <div>
                  <Radio value={ProductCategory.PO}>No, I need notice ahead of time</Radio>
                  <Radio
                    value={EInHomeProductCategory.IH_WITH_DATE}
                    // checked={productCategory === ProductCategory.IH}
                  >
                    Yes, it is available immediately but ask guest for date of use.
                  </Radio>
                  <Radio
                    value={EInHomeProductCategory.IH_WITHOUT_DATE}
                    // checked={field.value === ProductCategory.IH}
                  >
                    Yes, it is available immediately. Do not ask guest for date of use.
                  </Radio>
                  <Radio
                    value={ProductCategory.LINK}
                    disabled={paymentPlanOrder[plan] < paymentPlanOrder[PaymentPlan.Pro]}
                  >
                    This tile links to an external website.
                  </Radio>
                </div>
              </RadioGroup>
            </FormControl>
          );
        }}
      />
      {productCategory === ProductCategory.PO && (
        <>
          <Controller
            control={control}
            name="product.preorderBy"
            render={({ field: { value, ...restField }, fieldState: { error } }) => {
              return (
                <TextField
                  label="How many days notice do you need?"
                  required
                  type="number"
                  value={value ?? ''}
                  className="quantity-field"
                  {...restField}
                  error={!_.isUndefined(error)}
                  errorMessage={error?.message}
                />
              );
            }}
          />
          <Controller
            control={control}
            name="product.requiresApproval"
            render={({ field }) => {
              return (
                <FormControl label="Does this product require approval?" required>
                  <RadioGroup {...field}>
                    <div>
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </div>
                  </RadioGroup>
                </FormControl>
              );
            }}
          />
        </>
      )}

      {productCategory !== ProductCategory.LINK && (
        <>
          <Controller
            control={control}
            name="product.quantity"
            render={({ field: { ref: _ref, ...rest }, fieldState: { error } }) => (
              <QuantityField
                {...rest}
                error={!_.isUndefined(error)}
                errorMessage={error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="product.alertThreshold"
            render={({ field: { ref: _ref, ...rest }, fieldState: { error } }) => {
              return (
                <AlertTresholdField
                  {...rest}
                  error={!_.isUndefined(error)}
                  errorMessage={error?.message}
                />
              );
            }}
          />
          <Controller
            control={control}
            name="product.productDelegates"
            render={({ field: { value, onChange, name }, fieldState: { error } }) => {
              return (
                <DelegatesField
                  name={name}
                  value={value ?? []}
                  onChange={onChange}
                  error={!_.isUndefined(error)}
                  errorMessage={error?.message}
                />
              );
            }}
          />
          <Controller
            control={control}
            name="product.extraFields"
            render={({ field: { value, onChange, name }, fieldState: { error } }) => {
              return (
                <ExtraFields
                  name={name}
                  value={value ?? []}
                  onChange={onChange}
                  setError={setError}
                  error={!_.isUndefined(error)}
                  errorMessage={error?.message}
                />
              );
            }}
          />
        </>
      )}
      {productCategory === ProductCategory.LINK && (
        <Controller
          control={control}
          name="product.externalLink"
          render={({ field: { value, ref, ...rest }, fieldState: { error } }) => (
            <TextField
              label="What URL do you want to link to?"
              required
              helperText="This link should start with http or https."
              inputProps={{
                maxLength: 254,
                placeholder: 'https://example.com',
              }}
              inputRef={ref}
              value={value}
              className="external-link-field"
              {...rest}
              error={!_.isUndefined(error)}
              errorMessage={
                !_.isUndefined(error) && _.isNull(value) ? 'Required field.' : error?.message
              }
            />
          )}
        />
      )}
    </div>
  );
}
